// The stage against the real world: runs src/stage/state.ts with the store, reads the bring-back guard off the DOM,
// and gives the screens and the Director's directives one place to drive it.
import { get, set } from "../store";
import type { StageDirective } from "../types";
import { isBusy, reduceStage, stageContext, type FocusInfo, type StageEvent } from "./state";

export function dispatchStage(e: StageEvent) {
  const before = get().stage;
  const after = reduceStage(before, e);
  if (after !== before) set({ stage: after });
}

/** Sheets with unsaved edits mark themselves (`data-unsaved="true"` anywhere inside, or holdStage while open). */
let holds = 0;
export function holdStage(): () => void {
  holds++;
  let done = false;
  return () => {
    if (done) return;
    done = true;
    holds = Math.max(0, holds - 1);
  };
}

function focusInfo(): FocusInfo {
  if (typeof document === "undefined") return null;
  const el = document.activeElement as (HTMLElement & { type?: string; readOnly?: boolean; disabled?: boolean }) | null;
  if (!el || el === document.body) return null;
  // typing to the Director (the stage's own composer) never blocks the Director's stage
  if (el.closest?.(".stg")) return null;
  return { tag: el.tagName, type: el.getAttribute?.("type"), editable: el.isContentEditable, readOnly: !!el.readOnly, disabled: !!el.disabled };
}

function unsavedSheet(): boolean {
  if (holds > 0) return true;
  // the new-video sheet holds a brief being written
  if (get().newVideo) return true;
  return typeof document !== "undefined" && !!document.querySelector('[data-unsaved="true"]');
}

/** The bring-back guard (spec-v4 section 8.4): typing, or a sheet with unsaved edits open. */
export const userBusy = () => isBusy({ focus: focusInfo(), unsavedSheet: unsavedSheet() });

/** The `stage` directive (src/ui.ts). */
export function applyStageDirective(d: StageDirective) {
  dispatchStage({ type: "directive", d, busy: userBusy() });
}

export const minimiseStage = () => dispatchStage({ type: "minimise" });
export const backToStage = () => dispatchStage({ type: "restore" });
export const stageBack = () => dispatchStage({ type: "back" });
export const focusStage = (index: number) => dispatchStage({ type: "focus", index });

/** What the Director's tool calls can carry about the stage (V4-K1 adds it to runTool's context). */
export const currentStageContext = () => stageContext(get().stage);
